import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseHttpService } from './base-http-service';
import { TokenService } from './token.service';
import { ApiItemResponse, ApiListResponse } from '../models/api';
import { buildFilterParam } from '../utils/query';
import { ListParams } from '../interfaces/base-crud.interface';

@Injectable()
export abstract class BaseCrudService<T, U = T, F = T> extends BaseHttpService {
  protected abstract resource: string; // e.g. 'categories'
  protected apiRoot!: string; // e.g. `${environment.baseUrl}`

  constructor(
    protected httpClient: HttpClient,
    tokenService: TokenService,
  ) {
    super(httpClient, tokenService);
  }

  list({
    page,
    limit,
    sort,
    filter,
    where,
    include,
  }: ListParams<T> = {}): Observable<ApiListResponse<T>> {
    page = page || 1;
    limit = limit || 10;

    const search = Object.entries(filter || {})
      .filter(([_, val]: [string, string]) => !!val)
      .map(([key, val]: [string, any]) => {
        const op = val?.op || 'like';
        const value = typeof val === 'string' ? val : val?.val || '';
        return `where[${key}][${op}]=${value}`;
      });

    const whereSearch = Object.entries(where || {})
      .filter(([_, val]: [string, string]) => !!val)
      .map(([key, val]: [string, any]) => {
        const value = typeof val === 'string' ? val : val?.val || '';
        return `where[${key}]=${value}`;
      });
    const q = search.length > 0 ? `&${search.join('&')}` : '';
    const whereQ = whereSearch.length > 0 ? `&${whereSearch.join('&')}` : '';

    const order = Object.entries(sort || {}).map(
      ([key, val]: [string, string]) => `orderBy[${key}]=${val}`,
    );

    const orderBy = order.length > 0 ? `&${order.join('&')}` : '';

    const includeArr = (include || []).map(
      (col) => `include=${col.toString()}`,
    );

    const includes = includeArr.length > 0 ? `&${includeArr.join('&')}` : '';

    return this.http.get<ApiListResponse<T>>(
      `${this.apiRoot}/${this.resource}?page=${page}&limit=${limit}${includes}${orderBy}${whereQ}${q}`,
    );
  }

  list1(filter?: any): Observable<ApiListResponse<T>> {
    const q = buildFilterParam(filter);
    return this.http.get<ApiListResponse<T>>(
      `${this.apiRoot}/${this.resource}${q}`,
    );
  }

  getById(
    id: string,
    include: Array<keyof U | string> = [],
  ): Observable<ApiItemResponse<U>> {
    const includes = include
      .map((relation) => `include=${encodeURIComponent(relation.toString())}`)
      .join('&');
    const includeQuery = includes ? `?${includes}` : '';

    return this.http.get<ApiItemResponse<U>>(
      `${this.apiRoot}/${this.resource}/${id}${includeQuery}`,
    );
  }

  create(payload: Partial<F>): Observable<ApiItemResponse<T>> {
    return this.http.post<ApiItemResponse<T>>(
      `${this.apiRoot}/${this.resource}`,
      payload,
    );
  }

  update(id: string, payload: Partial<F>): Observable<ApiItemResponse<T>> {
    return this.http.patch<ApiItemResponse<T>>(
      `${this.apiRoot}/${this.resource}/${id}`,
      payload,
    );
  }

  delete(id: string): Observable<ApiItemResponse<T>> {
    return this.http.delete<ApiItemResponse<T>>(
      `${this.apiRoot}/${this.resource}/${id}`,
    );
  }
}
